import { useEffect, useState, useRef, useCallback } from 'react';

export interface UseCountdownProps {
  timeoutMs?: number;
  loop?: boolean;
  enabled: boolean;
  onFinish?: () => void;
}

const msToSecond = (ms: number) => Math.floor(ms / 1000);

export const useCountdown = ({
  timeoutMs = 0,
  loop = false,
  enabled,
  onFinish,
}: UseCountdownProps) => {
  const timerId = useRef<NodeJS.Timer>();

  const [countdown, setCountdown] = useState(msToSecond(timeoutMs));

  const restart = useCallback(() => {
    setCountdown(msToSecond(timeoutMs));
  }, [timeoutMs]);

  useEffect(() => {
    if (timeoutMs) {
      restart();
    }
  }, [restart, timeoutMs]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    timerId.current = setInterval(() => {
      setCountdown((countdown) => {
        if (countdown <= 0) {
          if (loop) {
            return msToSecond(timeoutMs);
          } else {
            clearInterval(timerId.current);
            onFinish?.();
            return 0;
          }
        }
        return countdown - 1;
      });
    }, 1000);

    return () => clearInterval(timerId.current);
  }, [enabled, loop, timeoutMs, onFinish]);

  const finished = countdown <= 0;

  return {
    finished,
    restart,
    countdown,
  };
};
